import React from "react";
import "./Avatar.css";

/**
 * Circular initials avatar with a deterministic brand gradient derived from a
 * seed string (so the same person is always the same color). Falls back to an
 * image when `src` is provided.
 *
 * Props:
 *   name:  seed string (used for initials + color)   required for initials
 *   src:   optional image URL
 *   size:  "sm" | "md" | "lg" | number (px)           (default "md")
 */
const SIZE_PX = { sm: 28, md: 40, lg: 56 };

// Each gradient carries the ink color that stays legible on it — dark ink on the
// light (gold / sky-mint) gradients, white on the dark/saturated ones.
const GRADIENTS = [
  { bg: "linear-gradient(135deg, #2ec4b6, #1a7a72)", ink: "#ffffff" },
  { bg: "linear-gradient(135deg, #ffe066, #ffb454)", ink: "#5b3d00" },
  { bg: "linear-gradient(135deg, #b3a3ff, #2ec4b6)", ink: "#ffffff" },
  { bg: "linear-gradient(135deg, #ff7a8a, #ffd0a0)", ink: "#5b1020" },
  { bg: "linear-gradient(135deg, #79c8ff, #b8f0d4)", ink: "#0f3460" },
  { bg: "linear-gradient(135deg, #6f7df2, #2ec4b6)", ink: "#ffffff" },
];

const hash = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h;
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const Avatar = ({ name = "", src, size = "md", className = "", style, ...rest }) => {
  const px = typeof size === "number" ? size : SIZE_PX[size] || SIZE_PX.md;
  const { bg, ink } = GRADIENTS[hash(name) % GRADIENTS.length];
  const darkInk = ink !== "#ffffff";

  return (
    <span
      className={`gx-avatar ${className}`}
      style={{
        width: px,
        height: px,
        fontSize: px * 0.4,
        background: src ? "none" : bg,
        color: ink,
        textShadow: darkInk ? "none" : "0 1px 2px rgba(0,0,0,0.25)",
        ...style,
      }}
      title={name || undefined}
      {...rest}
    >
      {src ? <img className="gx-avatar-img" src={src} alt={name} /> : getInitials(name)}
    </span>
  );
};

export default Avatar;
